import { useCallback, useEffect, useState } from "react";
import {
  applyTheme,
  nextTheme,
  readStoredTheme,
  resolveTheme,
  storeTheme,
  type ResolvedTheme,
  type ThemePreference,
} from "../lib/theme";

const QUERY = "(prefers-color-scheme: dark)";

/**
 * Current theme, and a toggle.
 *
 * Defaults to the system and keeps following it — including when it changes at
 * sunset — until someone picks a side, after which their choice sticks across
 * reloads and devices-per-browser.
 */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(() => readStoredTheme());
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(
    () => typeof window !== "undefined" && window.matchMedia(QUERY).matches,
  );

  // Keep following the OS while the preference is "system".
  useEffect(() => {
    const media = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const theme: ResolvedTheme = resolveTheme(preference, systemPrefersDark);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setPreference((current) => {
      const next = nextTheme(current, systemPrefersDark);
      storeTheme(next);
      return next;
    });
  }, [systemPrefersDark]);

  return { theme, preference, toggle, isFollowingSystem: preference === "system" };
}
